import ExclamationCircle from "@repo/icons/default/ExclamationCircle";
import Left from "@repo/icons/default/Left";
import User from "@repo/icons/default/User";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types";
import { z } from "zod";
import { CpsButtonText } from "corpus";
import { useAtomValue, useSetAtom } from "jotai";
import {
  UserQueryDocument,
  ValidateRecipientDataQueryDocument,
} from "@repo/graphql-types/graphql";
import {
  EmptyStateButton,
  EmptyStateDescription,
  EmptyStateRoot,
  EmptyStateTitle,
  EmptyStateIcon,
} from "@/components/ui/empty-state/empty-state";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { PendingComponent } from "@/components/ui/loading/pending-component";
import { formatRecipientDocument } from "@/lib/string";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import { confirmDocumentAtom } from "@/lib/atoms/confirm-document-atom";

import type { JSX } from "react";

const ConfirmDocumentPageQuery = graphql(/* GraphQL */ `
  query ConfirmDocumentPageQuery($codUsuario: Int!, $codClinica: Int! = 0) {
    usuarios(where: { codUsuario: { _eq: $codUsuario } }) {
      cpf
      nome
      sobrenome
    }
    clinicas: tbClinicas(where: { codClinica: { _eq: $codClinica } }) {
      cnpj
      razaoSocial
    }
  }
`);

const CriarClinicaPorCnpjMutation = graphql(/* GraphQL */ `
  mutation CriarClinicaPorCnpj($input: CriarClinicaPorCnpjInput!) {
    criarClinicaPorCnpj(input: $input) {
      criarClinicaPorCnpjOutput {
        codClinica
      }
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

enum ConfirmDocumentAction {
  IncorrectDocument = "incorrect-document",
}

const confirmDocumentSearchParamsSchema = z.object({
  action: z.string().optional(),
});

const ConfirmDocumentPage = (): JSX.Element => {
  const { user } = Route.useRouteContext();
  const router = useRouter();

  const queryResult = useGraphQL(ConfirmDocumentPageQuery, {
    codUsuario: user.codUsuario,
    codClinica: user.codClinica,
  });
  const { data } = queryResult;

  const { mutateAsync: criarClinica, isPending: isCreatingClinic } =
    useGraphQLMutationWithErrorHandler(CriarClinicaPorCnpjMutation);

  const { action } = useSearch({
    from: "/settings/payments/create-recipient/confirm-document",
  });

  const {
    cnpj: externalCnpj,
    nomeEmpresa,
    source,
    newClinicaId,
  } = useAtomValue(confirmDocumentAtom);
  const setConfirmDocumentAtom = useSetAtom(confirmDocumentAtom);

  const invalidateUserQuery = useInvalidateQuery(UserQueryDocument);
  const invalidateCreateRecipientQuery = useInvalidateQuery(
    ValidateRecipientDataQueryDocument,
  );

  // Se já criou clínica neste fluxo, ir direto para add-bank-account
  if (newClinicaId) {
    void router.navigate({
      to: "/settings/payments/create-recipient/add-bank-account",
      replace: true,
    });
  }

  const showIncorrectDocumentDrawer = action === ConfirmDocumentAction.IncorrectDocument;

  const setShowIncorrectDocumentDrawer = (show: boolean): void => {
    if (show) {
      void router.navigate({
        to: "/settings/payments/create-recipient/confirm-document",
        search: { action: ConfirmDocumentAction.IncorrectDocument },
      });
    } else if (showIncorrectDocumentDrawer) {
      router.history.back();
    }
  };

  const usuario = data?.usuarios[0];
  const clinica = data?.clinicas[0];

  const isCompany =
    source === "external" ? Boolean(externalCnpj) : Boolean(clinica?.cnpj);

  const documentType = isCompany ? "CNPJ" : "CPF";

  const getDocument = (): string | null | undefined => {
    if (source === "external") return externalCnpj;
    if (isCompany) return clinica?.cnpj;
    return usuario?.cpf;
  };

  const document = getDocument();

  const recipientType = isCompany ? "company" : "individual";

  const formattedDocument = formatRecipientDocument(recipientType, String(document));

  const getRecipientName = (): string | null | undefined => {
    if (source === "external") return nomeEmpresa;
    if (isCompany) return clinica?.razaoSocial;
    return `${usuario?.nome} ${usuario?.sobrenome}`;
  };

  const recipientName = getRecipientName();

  const title = `O ${documentType} cadastrado está correto?`;

  const titleDrawerText = isCompany
    ? "O CNPJ cadastrado deve coincidir com o CNPJ do titular."
    : "O CPF cadastrado na Livance deve coincidir com o CPF do titular da conta";

  const handleConfirm = async (): Promise<void> => {
    if (source === "external") {
      const result = await criarClinica({
        input: {
          cnpj: externalCnpj ?? "",
        },
      });

      if (result.criarClinicaPorCnpj.errors?.length) {
        return;
      }

      const codClinica = result.criarClinicaPorCnpj.criarClinicaPorCnpjOutput?.codClinica;

      if (codClinica) {
        invalidateUserQuery();
        invalidateCreateRecipientQuery();

        setConfirmDocumentAtom((prev) => ({
          ...prev,
          newClinicaId: codClinica,
        }));

        void router.navigate({
          to: "/settings/payments/create-recipient/add-bank-account",
          replace: true,
        });
      }
    } else {
      // Usuário já tem clínica, continuar fluxo normal
      void router.navigate({
        to: "/settings/payments/create-recipient/add-bank-account",
      });
    }
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Cadastrar conta" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <EmptyStateRoot>
              <EmptyStateIcon icon={User} />
              <EmptyStateDescription>
                <EmptyStateTitle>{title}</EmptyStateTitle>
                <div className="font-normal text-neutral-500 text-3xl pt-2">
                  {formattedDocument}
                </div>
                {recipientName ? (
                  <div className="text-md text-neutral-500 mt-2">{recipientName}</div>
                ) : null}
              </EmptyStateDescription>
              <EmptyStateButton
                fullWidth
                onClick={() => void handleConfirm()}
                loading={isCreatingClinic}
              >
                Sim
              </EmptyStateButton>
              <CpsButtonText onClick={() => setShowIncorrectDocumentDrawer(true)}>
                Não
              </CpsButtonText>
            </EmptyStateRoot>
          ) : null}
        </AsyncDataWrapper>
      </Page>

      <MessageDrawerRoot
        icon={ExclamationCircle}
        variant="secondary"
        open={showIncorrectDocumentDrawer}
        setOpen={setShowIncorrectDocumentDrawer}
      >
        <MessageDrawerTitle>{titleDrawerText}</MessageDrawerTitle>
        <MessageDrawerBody>
          Se houver qualquer divergência nas informações do seu cadastro, acione o time de
          Relacionamento.
        </MessageDrawerBody>
        <MessageDrawerActions>
          <a href="https://wa.me/551132304410" target="_blank" rel="noopener noreferrer">
            <MessageDrawerActionButton>Entrar em contato</MessageDrawerActionButton>
          </a>
          <MessageDrawerActionButton
            secondary
            onClick={() => setShowIncorrectDocumentDrawer(false)}
          >
            Voltar
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </>
  );
};

export const Route = createFileRoute(
  "/settings/payments/create-recipient/confirm-document",
)({
  component: ConfirmDocumentPage,
  pendingComponent: () => <PendingComponent title="Cadastrar conta" />,
  validateSearch: (search) => confirmDocumentSearchParamsSchema.parse(search),
});
